import { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import DigitalStudio from "@/digital-studio";
import { FloatingRibbonNav } from "@/components/FloatingRibbonNav";

const SITE_ORIGIN = "https://fortebyryann.com";
const SOCIAL_IMAGE = `${SITE_ORIGIN}/media/hero-poster.jpg`;
const KNOWN_ROUTES = new Set(["/", "/digital-studio"]);

const ROUTE_METADATA = {
  home: {
    path: "/",
    title: "Forte by Ryann | Digital Pilates & Strength",
    socialTitle: "Forte by Ryann",
    description:
      "Train with Ryann through Forte Digital Studio, private training, events, and a method blending athletic Pilates, mobility, and progressive strength.",
  },
  digitalStudio: {
    path: "/digital-studio",
    title: "Forte Digital Studio | Live & On-Demand Workouts",
    socialTitle: "Forte Digital Studio | Train with Ryann Anywhere",
    description:
      "Train with Ryann anywhere with six weekly live classes, a membership-only video library, and published replays for $45 per month.",
  },
};

let locationSequence = 0;

function normalizePathname(pathname) {
  const withoutTrailingSlash = pathname.replace(/\/+$/, "");
  return withoutTrailingSlash || "/";
}

function routeForPathname(pathname) {
  return normalizePathname(pathname) === "/digital-studio"
    ? "digitalStudio"
    : "home";
}

function readLocation({ normalize = false } = {}) {
  const normalizedPathname = normalizePathname(window.location.pathname);

  if (
    normalize &&
    KNOWN_ROUTES.has(normalizedPathname) &&
    normalizedPathname !== window.location.pathname
  ) {
    window.history.replaceState(
      window.history.state,
      "",
      `${normalizedPathname}${window.location.search}${window.location.hash}`,
    );
  }

  return {
    pathname: normalizedPathname,
    search: window.location.search,
    hash: window.location.hash,
    sequence: ++locationSequence,
  };
}

function setMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function applyRouteMetadata(pathname) {
  const metadata = ROUTE_METADATA[routeForPathname(pathname)];
  const canonicalUrl = `${SITE_ORIGIN}${metadata.path}`;
  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  document.title = metadata.title;
  canonical.setAttribute("href", canonicalUrl);
  setMeta("name", "description", metadata.description);
  setMeta("property", "og:title", metadata.socialTitle);
  setMeta("property", "og:description", metadata.description);
  setMeta("property", "og:type", "website");
  setMeta("property", "og:site_name", "Forte by Ryann");
  setMeta("property", "og:url", canonicalUrl);
  setMeta("property", "og:image", SOCIAL_IMAGE);
  setMeta("property", "og:image:width", "1920");
  setMeta("property", "og:image:height", "1080");
  setMeta(
    "property",
    "og:image:alt",
    "Ryann Deering training in the Forte studio",
  );
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", metadata.socialTitle);
  setMeta("name", "twitter:description", metadata.description);
  setMeta("name", "twitter:image", SOCIAL_IMAGE);
  setMeta(
    "name",
    "twitter:image:alt",
    "Ryann Deering training in the Forte studio",
  );
}

function scrollToHash(hash) {
  if (!hash || hash === "#") return false;

  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch (error) {}

  const target = document.getElementById(id);
  if (!target) return false;

  const top = Math.max(
    0,
    target.getBoundingClientRect().top + window.scrollY - 96,
  );
  window.scrollTo({ top, left: 0, behavior: "auto" });
  return true;
}

const initialLocation = readLocation({ normalize: true });
applyRouteMetadata(initialLocation.pathname);

function RouteSelection() {
  const [currentLocation, setCurrentLocation] = useState(initialLocation);
  const [navReady, setNavReady] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setNavReady(true), 150);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const syncLocation = useCallback(() => {
    setCurrentLocation(readLocation({ normalize: true }));
  }, []);

  const navigate = useCallback((href, event) => {
    let target;
    try {
      target = new URL(href, window.location.href);
    } catch (error) {
      return;
    }

    const normalizedPathname = normalizePathname(target.pathname);
    if (
      target.origin !== window.location.origin ||
      !KNOWN_ROUTES.has(normalizedPathname)
    ) {
      return;
    }

    event?.preventDefault();
    const nextUrl = `${normalizedPathname}${target.search}${target.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const pathChanged =
      normalizedPathname !== normalizePathname(window.location.pathname);

    const commitNavigation = () => {
      if (nextUrl !== currentUrl) {
        window.history.pushState({}, "", nextUrl);
      }

      const nextLocation = readLocation({ normalize: true });
      flushSync(() => setCurrentLocation(nextLocation));

      if (nextLocation.hash) {
        scrollToHash(nextLocation.hash);
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    };

    commitNavigation();
  }, []);

  useEffect(() => {
    window.addEventListener("popstate", syncLocation);
    window.addEventListener("hashchange", syncLocation);
    return () => {
      window.removeEventListener("popstate", syncLocation);
      window.removeEventListener("hashchange", syncLocation);
    };
  }, [syncLocation]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const anchor =
        event.target instanceof Element
          ? event.target.closest("a[href]")
          : null;
      if (
        !anchor ||
        anchor.hasAttribute("download") ||
        (anchor.target && anchor.target !== "_self")
      ) {
        return;
      }

      navigate(anchor.href, event);
    };

    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, [navigate]);

  useEffect(() => {
    applyRouteMetadata(currentLocation.pathname);
  }, [currentLocation.pathname]);

  useEffect(() => {
    if (!currentLocation.hash) return undefined;

    let cancelled = false;
    let frameId;
    let timeoutId;
    let attempts = 0;

    const attemptScroll = () => {
      if (cancelled) return;
      if (scrollToHash(currentLocation.hash)) return;

      attempts += 1;
      if (attempts < 40) {
        timeoutId = window.setTimeout(attemptScroll, 50);
      }
    };

    frameId = window.requestAnimationFrame(attemptScroll);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [
    currentLocation.hash,
    currentLocation.pathname,
    currentLocation.sequence,
  ]);

  return (
    <>
      <FloatingRibbonNav
        pathname={currentLocation.pathname}
        ready={navReady}
      />
      {routeForPathname(currentLocation.pathname) === "digitalStudio" ? (
        <DigitalStudio />
      ) : (
        <App onNavigate={navigate} />
      )}
    </>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<RouteSelection />);
